import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { dayLabel, dayTone, fmtDay, metaFor, prettyMinutes, weekOf } from '@/lib/plannerUtils';

/** The week at a glance — seven cards, the way a student plans a Sunday. */
export function WeekTab({ plan, anchor, onShift, onToggleDone }) {
  const week = weekOf(anchor);
  const byDate = Object.fromEntries((plan?.days || []).map(d => [d.date, d]));

  return (
    <div data-testid="planner-week">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-heading text-xl font-bold text-slate-900">
            {fmtDay(week[0], { day: 'numeric', month: 'short' })} – {fmtDay(week[6], { day: 'numeric', month: 'short' })}
          </h3>
          <p className="font-body text-xs text-slate-400">
            {plan?.revision_from && week[6] >= plan.revision_from ? 'Revision window' : 'Build phase'}
          </p>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => onShift(-7)} data-testid="planner-prev-week"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 hover:bg-slate-50">
            <ChevronLeft className="w-3 h-3" /> Previous
          </button>
          <button onClick={() => onShift(0)} data-testid="planner-this-week"
            className="px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 hover:bg-slate-50">
            This week
          </button>
          <button onClick={() => onShift(7)} data-testid="planner-next-week"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-heading text-slate-600 hover:bg-slate-50">
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mt-4">
        {week.map(iso => {
          const day = byDate[iso];
          const items = day?.items || [];
          const done = items.filter(i => i.done).length;
          return (
            <div key={iso} data-testid={`planner-week-day-${iso}`}
              className={`rounded-2xl border bg-white p-3 ${dayTone(day)} ${iso === plan?.today ? 'ring-2 ring-indigo-300' : ''}`}>
              <div className="flex items-baseline justify-between">
                <p className="font-heading text-lg font-bold text-slate-900">
                  {new Date(`${iso}T00:00:00`).getDate()}
                  <span className="ml-1.5 text-[10px] uppercase tracking-widest text-slate-400">
                    {fmtDay(iso, { weekday: 'short' })}
                  </span>
                </p>
                <span className="font-body text-[11px] text-slate-400">{items.length ? `${done}/${items.length}` : '—'}</span>
              </div>
              <p className="font-body text-[11px] text-slate-400 mt-0.5">
                {items.length ? `${dayLabel(day)} · ${prettyMinutes(day.minutes)}` : 'Free day'}
              </p>
              <div className="mt-2 space-y-1.5">
                {items.map(item => {
                  const Icon = metaFor(item).icon;
                  return (
                    <button key={item.ref} onClick={() => onToggleDone(item.ref, !item.done)}
                      data-testid={`planner-week-item-${item.ref}`}
                      className="w-full flex items-start gap-1.5 text-left group">
                      <span className={`w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center flex-shrink-0 ${item.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 group-hover:border-emerald-400'}`}>
                        {item.done && <Check className="w-2.5 h-2.5" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={`block font-body text-[12px] leading-snug ${item.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                          {item.label}
                        </span>
                        <span className="flex items-center gap-1 font-body text-[10px] text-slate-400">
                          <Icon className="w-2.5 h-2.5" /> {item.subject} · {prettyMinutes(item.minutes)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WeekTab;
